import { useState } from 'react'
import { Alert, Button, Drawer, Group, Loader, Select, TextInput, Tooltip } from '@mantine/core'
import { useDebouncedValue } from '@mantine/hooks'
import { IconSearch } from '@tabler/icons-react'
import InfiniteScroll from 'react-infinite-scroll-component'
import type { PopulationRiskLevel } from '@behuman/core-contract'
import { InfoIcon } from '../components/InfoIcon'
import { formatCount } from '../format'
import { useCohort, useCohortExport, type CohortTarget, type PopulationQuery } from '../hooks'
import { RISK_LEVEL_LABELS, options } from '../labels'
import { CohortPatientRow } from './CohortPatientRow'

const SCROLL_TARGET = 'population-cohort-scroll'
const ALL = 'all'
const REACHABLE = 'reachable'

function FilterCard({
  label,
  value,
  active,
  disabled = false,
  onClick,
}: {
  label: string
  value: number | undefined
  active: boolean
  disabled?: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      disabled={disabled}
      onClick={onClick}
      className={`flex-1 rounded-md border p-2 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${active ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}
    >
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-lg font-semibold tabular-nums">
        {value === undefined ? '…' : formatCount(value)}
      </div>
    </button>
  )
}

/**
 * The cohort drawer: one measure's or one family group's patients under the page's filters,
 * narrowed further by risk, reachability and name. It can only narrow: a page-level risk level
 * locks the drawer's own. Loads more rows on scroll until `page.total` are shown. Remount it
 * (key) per target and page risk level so its own filters start fresh.
 *
 * Anyone can exclude patients (PD-1992) — kept here, in the browser only — and export the
 * rest as CSV (PD-1775). The selection is the cohort under the page filters; the drawer's own
 * risk, reachability and search controls only narrow the list. Launch campaign is shown
 * disabled: campaigns are coming later.
 */
export function CohortDrawer({
  target,
  query,
  onClose,
}: {
  target: CohortTarget | null
  query: PopulationQuery
  onClose: () => void
}) {
  /** The patients excluded from the selection, by id. */
  const [excluded, setExcluded] = useState<ReadonlySet<string>>(() => new Set())
  const exclude = (patientId: string) => setExcluded((prev) => new Set(prev).add(patientId))
  const include = (patientId: string) =>
    setExcluded((prev) => {
      const next = new Set(prev)
      next.delete(patientId)
      return next
    })
  const exportCohort = useCohortExport()
  // The page's risk level, when set, is in force here too and can't be widened or swapped.
  const pageRisk = query.riskLevel
  const [drawerRisk, setDrawerRisk] = useState<PopulationRiskLevel | undefined>()
  const riskLevel = pageRisk ?? drawerRisk
  const [reachableOnly, setReachableOnly] = useState(false)
  const [search, setSearch] = useState('')
  const [debouncedSearch] = useDebouncedValue(search.trim(), 300)

  const cohort = useCohort(target, {
    providerId: query.providerId,
    state: query.state,
    ageGroup: query.ageGroup,
    kind: query.kind,
    riskLevel,
    reachableOnly: reachableOnly || undefined,
    search: debouncedSearch || undefined,
  })
  const pages = cohort.data?.pages ?? []
  const first = pages[0]
  const patients = pages.flatMap((page) => page.data)
  const total = pages.at(-1)?.page.total ?? 0
  // The previous filters' rows, still shown while the new ones load: dimmed and marked busy.
  const stale = cohort.isPlaceholderData

  // The cards, and the selection, are the cohort under the page's filters alone. The
  // server counts totals after the drawer's own filters too, so read them from the unfiltered
  // query (the drawer's first request, so normally cached) — never from a filtered response,
  // even when a filter is chosen before the unfiltered one has answered (PD-1913).
  const unfiltered = useCohort(target, {
    providerId: query.providerId,
    state: query.state,
    ageGroup: query.ageGroup,
    kind: query.kind,
    riskLevel: pageRisk,
  })
  const totals = unfiltered.isPlaceholderData ? undefined : unfiltered.data?.pages[0]?.cohort.totals

  // Back to the page's own filters.
  const reset = () => {
    setDrawerRisk(undefined)
    setReachableOnly(false)
  }

  const cohortLabel = first?.cohort.label ?? (cohort.isPending ? 'Loading…' : 'Patients')
  const selected = totals ? Math.max(0, totals.patients - excluded.size) : 0

  return (
    <Drawer
      opened={target !== null}
      onClose={onClose}
      position="right"
      size="50%"
      title={<span className="text-xl font-semibold">{cohortLabel}</span>}
    >
      <>
        {first && (
          <p className="mb-3 border-t border-gray-100 pt-3 text-sm text-gray-600">
            {first.cohort.definition}
          </p>
        )}

        <div
          aria-busy={stale}
          className={`mb-3 flex gap-2 ${stale ? 'opacity-50 transition-opacity' : ''}`}
        >
          <FilterCard
            label="Patients"
            value={totals?.patients}
            active={drawerRisk === undefined && !reachableOnly}
            onClick={reset}
          />
          <FilterCard
            label="High risk"
            value={totals?.highRisk}
            active={riskLevel === 'high'}
            disabled={pageRisk !== undefined}
            onClick={() => setDrawerRisk('high')}
          />
          <FilterCard
            label="Reachable"
            value={totals?.reachable}
            active={reachableOnly}
            onClick={() => setReachableOnly(true)}
          />
        </div>
        {/* The totals have their own request: without them the selection is unknown, so say why. */}
        {unfiltered.isError && !cohort.isError && (
          <Alert color="red" variant="light" mb="sm" title="Couldn’t load the cohort’s totals">
            <Button size="xs" variant="light" color="red" onClick={() => unfiltered.refetch()}>
              Try again
            </Button>
          </Alert>
        )}

        <Group gap="xs" mb="sm" align="flex-end" wrap="nowrap">
          <Select
            label={
              <span className="inline-flex items-center">
                Risk
                {first && <InfoIcon label="Risk" definition={first.definitions.riskLevel} />}
              </span>
            }
            size="xs"
            w={130}
            allowDeselect={false}
            disabled={pageRisk !== undefined}
            description={pageRisk ? 'Set by the page filter' : undefined}
            data={[
              { value: ALL, label: 'All' },
              ...options<PopulationRiskLevel>(RISK_LEVEL_LABELS),
            ]}
            value={riskLevel ?? ALL}
            onChange={(value) =>
              setDrawerRisk(value === ALL || !value ? undefined : (value as PopulationRiskLevel))
            }
          />
          <Select
            label={
              <span className="inline-flex items-center">
                Reachability
                {first && (
                  <InfoIcon label="Reachability" definition={first.definitions.reachable} />
                )}
              </span>
            }
            size="xs"
            w={140}
            allowDeselect={false}
            data={[
              { value: ALL, label: 'All' },
              { value: REACHABLE, label: 'Reachable' },
            ]}
            value={reachableOnly ? REACHABLE : ALL}
            onChange={(value) => setReachableOnly(value === REACHABLE)}
          />
          <TextInput
            size="xs"
            className="flex-1"
            aria-label="Search by name"
            placeholder="Search by name"
            leftSection={<IconSearch size={14} />}
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
          />
        </Group>

        {cohort.isError ? (
          <Alert color="red" variant="light" title="Couldn’t load the patients">
            <Button
              size="xs"
              variant="light"
              color="red"
              onClick={() => {
                void cohort.refetch()
                if (unfiltered.isError) void unfiltered.refetch()
              }}
            >
              Try again
            </Button>
          </Alert>
        ) : cohort.isPending ? (
          <div className="flex justify-center py-8">
            <Loader size="sm" aria-label="Loading patients" />
          </div>
        ) : patients.length === 0 ? (
          <p className="py-6 text-center text-sm text-gray-500">No patients match these filters.</p>
        ) : (
          <div
            id={SCROLL_TARGET}
            aria-busy={stale}
            className={`h-[calc(100vh-420px)] min-h-[240px] overflow-y-auto ${stale ? 'opacity-50 transition-opacity' : ''}`}
          >
            <InfiniteScroll
              dataLength={patients.length}
              next={() => cohort.fetchNextPage()}
              hasMore={cohort.hasNextPage}
              loader={
                <Group justify="center" p="sm">
                  <Loader size="sm" aria-label="Loading more patients" />
                </Group>
              }
              scrollableTarget={SCROLL_TARGET}
            >
              <ul aria-label="Cohort patients">
                {patients.map((patient) => (
                  <CohortPatientRow
                    key={patient.patientId}
                    patient={patient}
                    excluded={excluded.has(patient.patientId)}
                    onExclude={() => exclude(patient.patientId)}
                    onInclude={() => include(patient.patientId)}
                  />
                ))}
              </ul>
            </InfiniteScroll>
          </div>
        )}

        <p
          aria-busy={stale}
          className={`mt-3 flex items-center gap-2 border-t border-gray-100 pt-2 text-sm text-gray-600 ${stale ? 'opacity-50' : ''}`}
        >
          {stale && <Loader size="xs" aria-label="Updating patients" />}
          {cohort.isPending
            ? '…'
            : `Showing ${formatCount(patients.length)} of ${formatCount(total)} patients`}
        </p>

        {target && (
          <div className="mt-2 flex items-center justify-between gap-3 border-t border-gray-100 pt-3">
            <span aria-label="Selection" className="text-sm text-gray-700">
              {totals === undefined ? '…' : `${formatCount(selected)} selected`}
              {excluded.size > 0 && ` · ${formatCount(excluded.size)} excluded`}
            </span>
            <Group gap="xs">
              <Button
                size="sm"
                variant="default"
                loading={exportCohort.isPending}
                onClick={() =>
                  exportCohort.mutate({
                    target: target.type === 'family' ? 'group' : 'measure',
                    slug: target.slug,
                    providerId: query.providerId,
                    state: query.state,
                    ageGroup: query.ageGroup,
                    riskLevel: query.riskLevel,
                    kind: query.kind,
                    excludedPatientIds: [...excluded],
                  })
                }
              >
                Export cohort
              </Button>
              {/* Campaigns are coming later (PD-1992): shown, disabled. data-disabled (not
                  disabled) keeps hover, so the tooltip can show; aria-disabled tells screen
                  readers it is unavailable. */}
              <Tooltip label="Coming soon">
                <Button size="sm" data-disabled aria-disabled="true">
                  Launch campaign
                </Button>
              </Tooltip>
            </Group>
          </div>
        )}
        {exportCohort.isError && (
          <Alert
            color="red"
            variant="light"
            mt="sm"
            withCloseButton
            onClose={() => exportCohort.reset()}
          >
            Couldn’t export the cohort. Try again.
          </Alert>
        )}
      </>
    </Drawer>
  )
}
